import { Dismiss20Regular } from '@fluentui/react-icons';
import {
  FinishingActionsContainer,
  TicketsDiscardButton,
  TicketsSaveButton,
  StyledTextarea,
  TicketsAddBackdrop,
  TicketsAddForm,
  TicketsAddPopupOverlay,
  TicketsMiddleContainer,
  TicketsTopContainer,
} from './TicketsAddPopup.styled';
import { Field } from '@fluentui/react-components';
import { FormDropdown } from '../FormDropdown';
import {
  CreateTicket,
  OptionSet,
} from 'src/components/Tickets/models/Tickets.interface';
import {
  AlertIcon,
  FormSectionTitle,
  FormTitle,
} from '../styles/common.styled';
import { SubmitHandler, useForm } from 'react-hook-form';
import { FormDatePicker } from '../FormDatePicker';
import { Alert } from 'src/components/common/AlertMessage/AlertMessage';
import { DatePickerStyles } from 'src/styles/globalStyles/datePickerStyles.styled';
import { TeamsFxContext } from 'src/context/TeamsContext';
import { useContext, useEffect } from 'react';
import { useServiceDeskContext } from 'src/context/ServiceDesk/ServiceDeskContext';
import { singlePubSub, singlePubSubConst } from 'src/pub-sub/pub-sub';

interface TicketsAddPopupProps {
  createTicket: (params: CreateTicket) => void;
  isLoadingCreateTicket: boolean;
  ticketCategories: OptionSet[];
  ticketTypes: OptionSet[];
  ticketApprovalStatuses: OptionSet[];
  ticketPriorities: OptionSet[];
  handleSubmittedTicket: () => void;
}

export type Inputs = {
  description: string;
  ticketType: OptionSet;
  ticketCategory: OptionSet;
  ticketPriority: OptionSet;
  requestDueDate: Date;
};

const defaultValues = {
  description: '',
  ticketCategory: { label: '', value: '' },
  ticketPriority: { value: '100000001', label: 'Medium' },
  ticketType: { label: '', value: '' },
  requestDueDate: null,
};

export const TicketsAddPopup = ({
  ticketTypes,
  ticketCategories,
  ticketPriorities,
  createTicket,
  isLoadingCreateTicket,
  handleSubmittedTicket,
}: TicketsAddPopupProps) => {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
    control,
    setValue,
    watch,
  } = useForm<Inputs>({
    defaultValues,
    shouldFocusError: false,
    shouldUnregister: false,
  });
  const descriptionWatch = watch('description');
  const { toggleAddTicketPopup } = useServiceDeskContext();
  const { themeString, isMobile } = useContext(TeamsFxContext);

  const resizeTextField = () => {
    const element = document.querySelector(
      'textarea[name="description"]'
    ) as HTMLTextAreaElement;

    if (element) {
      element.style.height = '0px';

      const scrollHeight = element.scrollHeight;

      element.style.height = `${scrollHeight}px`;
    }
  };

  useEffect(() => {
    resizeTextField();
  }, [descriptionWatch]);

  useEffect(() => {
    return () => {
      singlePubSub.unsubscribe(singlePubSubConst.RESET_DATE_PICKER);
    };
  }, []);

  const handleResetAll = () => {
    reset(defaultValues);
    setValue('ticketPriority', { value: '', label: '' });
    singlePubSub.notify(singlePubSubConst.RESET_DATE_PICKER);
  };

  const onClose = () => {
    toggleAddTicketPopup(false);
  };

  const formErrors =
    (errors.description && errors.description?.message === '') ||
    errors.ticketCategory ||
    errors.ticketPriority ||
    errors.ticketType;
  const onSubmit: SubmitHandler<Inputs> = async (data) => {
    await createTicket({
      new_tickettype: data.ticketType.value,
      _new_category_value: data.ticketCategory.value,
      new_priority: data.ticketPriority.value,
      new_description: data.description,
      new_requestedduedate: data.requestDueDate,
    });
    handleSubmittedTicket();
    onClose();
  };

  return (
    <>
      <DatePickerStyles />
      {!isMobile && <TicketsAddBackdrop />}
      <TicketsAddPopupOverlay>
        <TicketsAddForm onSubmit={handleSubmit(onSubmit)}>
          <TicketsTopContainer themeString={themeString}>
            <FormTitle>Submit new ticket</FormTitle>
            {!isMobile && <Dismiss20Regular onClick={onClose} />}
          </TicketsTopContainer>
          <TicketsMiddleContainer>
            {formErrors && (
              <Alert
                requestStatusAppearance={'error'}
                requestStatusMessage={'Please fill in all the required fields.'}
                showCloseButton={false}
                handleClose={null}
              />
            )}
            <FormDropdown
              name="ticketType"
              error={errors.ticketType}
              rules={{
                required: true,
                validate: (value) => value.value !== '',
              }}
              controlName="ticketType"
              control={control}
              title="Ticket type"
              options={ticketTypes}
              labelId="ticket-type"
              placeholder="Select ticket type"
            />
            <FormDropdown
              name="ticketCategory"
              error={errors.ticketCategory}
              rules={{
                required: true,
                validate: (value) => value.value !== '',
              }}
              controlName="ticketCategory"
              control={control}
              title="Category"
              options={ticketCategories}
              labelId="ticket-category"
              placeholder="Select category"
            />
            <FormDropdown
              name="ticketPriority"
              error={errors.ticketPriority}
              rules={{
                required: true,
                validate: (value) => value.value !== '',
              }}
              controlName="ticketPriority"
              control={control}
              title="Priority"
              options={ticketPriorities}
              labelId="ticket-priority"
              placeholder="Select priority"
            />
            <FormSectionTitle isRequired>
              {errors.description && <AlertIcon />}Description
            </FormSectionTitle>
            <Field validationMessage={errors.description?.message}>
              <StyledTextarea
                error={errors.description}
                size="large"
                {...register('description', {
                  required: true,
                  minLength: {
                    value: 5,
                    message:
                      'Description should be at least 5 characters long.',
                  },
                  pattern: {
                    value: /\S+/,
                    message: 'Please input valid description message.',
                  },
                })}
                onFocus={resizeTextField}
                name="description"
                placeholder="Please describe your request here."
                appearance="filled-darker"
              />
            </Field>
            <FormDatePicker controlName="requestDueDate" control={control} />
          </TicketsMiddleContainer>
          <FinishingActionsContainer themeString={themeString}>
            <p onClick={handleResetAll}>Clear all</p>
            <TicketsDiscardButton
              disabled={isLoadingCreateTicket}
              onClick={onClose}
            >
              Discard
            </TicketsDiscardButton>
            <TicketsSaveButton type="submit" disabled={false}>
              Submit
            </TicketsSaveButton>
          </FinishingActionsContainer>
        </TicketsAddForm>
      </TicketsAddPopupOverlay>
    </>
  );
};
